// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { Box, Button, ButtonToggle, Flex, useToast } from '@iheartjane/reefer';
import type {
  ColumnFiltersState,
  RowSelectionState,
} from '@tanstack/react-table';
import capitalize from 'lodash-es/capitalize';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';

import {
  useArchiveUnpublishedProduct,
  useAutoPublishSettings,
  useBulkPublishProducts,
  useStoreSettings,
  useUpdateBulkProducts,
} from '@jane/business-admin/data-access';
import {
  useModalActionsWithTracking,
  useNavigateAndTrack,
} from '@jane/business-admin/hooks';
import { ProductsTableContext } from '@jane/business-admin/providers';
import {
  EventNames,
  ModalNames,
  NavigationSourceIds,
  businessPaths,
  track,
} from '@jane/business-admin/util';
import type { ValueOf } from '@jane/business-admin/util';

import { ButtonToggleStylesOverrideWrapper } from '../../../../shared/tables/ButtonToggleStylesOverrideWrapper';
import { AutoPublishModal } from '../modal/AutoPublishModal';
import { ClearModal } from '../modal/ClearModal';
import { RevertModal } from '../modal/RevertModal';
import { BulkEditModal } from './BulkEditModal';
import { HiddenProductTable } from './HiddenProductTable';
import { PublishedProductTable } from './PublishedProductTable';
import { UnpublishedProductTable } from './UnpublishedProductTable';

const AutoPublishButtonWrapper = styled.div({
  minWidth: '181px',
});

const BulkEditButtonWrapper = styled.div({
  minWidth: '99px',
});

const ClearPotenciesButtonWrapper = styled.div({
  minWidth: '99px',
});

const ProductTableType = {
  published: 'Published',
  hidden: 'Hidden',
  unpublished: 'Unpublished',
} as const;

type ProductTableType = ValueOf<typeof ProductTableType>;

const isProductTableType = (test: unknown): test is ProductTableType => {
  return (
    typeof test === 'string' &&
    (Object.values(ProductTableType) as string[]).includes(test)
  );
};

const HeaderButton = ({
  onClick,
  selectedProducts,
  text,
  productTableType,
}: {
  onClick: any;
  productTableType: ProductTableType;
  selectedProducts: any;
  text: string;
}) => {
  const showRevertButton =
    useFlag(FLAGS.cnBulkRevertProductImagesDescriptions) &&
    (productTableType === ProductTableType.published ||
      productTableType === ProductTableType.hidden);
  const selectedLength = Object.keys(selectedProducts).length;
  const hasSelected = selectedLength !== 0;
  const label = (buttonText: string) =>
    hasSelected ? `${buttonText} (${selectedLength})` : buttonText;

  return (
    <>
      {text === 'Publish' ? (
        <Button
          mr={16}
          label={label('Archive')}
          variant="secondary"
          disabled={!hasSelected}
          onClick={() => onClick('Archive')}
        />
      ) : null}
      {showRevertButton ? (
        <Button
          mr={16}
          label={label('Revert')}
          variant="secondary"
          disabled={!hasSelected}
          onClick={() => onClick('Revert')}
        />
      ) : null}
      <Button
        label={label(text)}
        variant="secondary"
        disabled={!hasSelected}
        onClick={onClick}
      />
    </>
  );
};

const buttonTextMapping: Record<ProductTableType, string> = {
  [ProductTableType.published]: 'Hide',
  [ProductTableType.hidden]: 'Make visible',
  [ProductTableType.unpublished]: 'Publish',
} as const;

export const ProductTable = () => {
  const { id: storeId = '' } = useParams<'id'>();
  const { canEditProducts } = useContext(ProductsTableContext);
  const navigate = useNavigateAndTrack();

  const { type = 'published' } = useParams<'type'>();
  const { product_id = '' } = useParams<'product_id'>();
  const [selected, setSelected] = useState<ProductTableType>(
    (capitalize(type) as ProductTableType) || ProductTableType.published
  );
  const [selectedProducts, setSelectedProducts] = useState<RowSelectionState>(
    {}
  );
  const [fullSelectedProducts, setFullSelectedProducts] = useState<any>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const showClearButton = useFlag(FLAGS.cnBulkClearProductPotencies);

  const hasArchivedFilter = useMemo(() => {
    return columnFilters.some((filter) => {
      return (
        filter.id === 'status' &&
        (filter.value as string[]).includes('archived')
      );
    });
  }, [JSON.stringify(columnFilters)]);

  // When tab is selected, update URL
  useEffect(() => {
    navigate(
      `${businessPaths.storeProducts(storeId, selected.toLowerCase())}${
        product_id ? '/' + product_id : ''
      }`,
      NavigationSourceIds.TableToggle,
      storeId
    );
  }, [selected, storeId]);

  // When page is loaded with tab in URL, update selected tab
  useEffect(() => {
    if (type === 'unpublished') {
      setSelected(ProductTableType.unpublished);
    } else if (type === 'hidden') {
      setSelected(ProductTableType.hidden);
    } else {
      setSelected(ProductTableType.published);
    }
  }, []);

  const toast = useToast();
  const { data: storePayload } = useStoreSettings(storeId);
  const { data: autoPublishSettings } = useAutoPublishSettings(storeId);
  const {
    modalOpen: autoPublishModalOpen,
    openModal: setAutoPublishModalOpen,
    closeModal: setAutoPublishModalClose,
  } = useModalActionsWithTracking(ModalNames.ProductAutoPublish);
  const {
    modalOpen: bulkEditModalOpen,
    openModal: setBulkEditModalOpen,
    closeModal: setBulkEditModalClose,
  } = useModalActionsWithTracking(ModalNames.BulkEditProducts);
  const {
    modalOpen: revertModalOpen,
    openModal: setRevertModalOpen,
    closeModal: setRevertModalClose,
  } = useModalActionsWithTracking(ModalNames.BulkEditProducts);
  const {
    modalOpen: clearModalOpen,
    openModal: setClearModalOpen,
    closeModal: setClearModalClose,
  } = useModalActionsWithTracking(ModalNames.BulkEditProducts);
  const {
    mutateAsync: bulkUpdateProducts,
    isLoading: bulkUpdateProductsLoading,
    isSuccess: bulkUpdateProductsSuccess,
    isError: bulkUpdateProductsError,
  } = useUpdateBulkProducts(storeId);
  const {
    mutateAsync: bulkPublishProducts,
    isLoading: bulkPublishProductsLoading,
    isSuccess: bulkPublishProductsSuccess,
    isError: bulkPublishProductsError,
  } = useBulkPublishProducts(storeId);
  const {
    mutateAsync: bulkArchiveProducts,
    isLoading: bulkArchiveProductsLoading,
    isSuccess: bulkArchiveProductsSuccess,
    isError: bulkArchiveProductsError,
  } = useArchiveUnpublishedProduct(storeId);

  const trackSubmit = (action: string) => {
    track({
      event: EventNames.BulkEdited,
      objects: Object.keys(selectedProducts),
      action: action,
    });
  };

  const table = (isBulkEditTable = false) =>
    selected === ProductTableType.published ? (
      <PublishedProductTable
        isBulkEditTable={isBulkEditTable}
        selectedProducts={selectedProducts}
        setSelectedProducts={setSelectedProducts}
        setColumnFiltersParent={setColumnFilters}
        columnFiltersParent={columnFilters}
        bulkEditModalOpen={bulkEditModalOpen}
      />
    ) : selected === ProductTableType.hidden ? (
      <HiddenProductTable
        isBulkEditTable={isBulkEditTable}
        selectedProducts={selectedProducts}
        setSelectedProducts={setSelectedProducts}
        setColumnFiltersParent={setColumnFilters}
        columnFiltersParent={columnFilters}
        bulkEditModalOpen={bulkEditModalOpen}
      />
    ) : selected === ProductTableType.unpublished ? (
      <UnpublishedProductTable
        selectedProducts={selectedProducts}
        setSelectedProducts={setSelectedProducts}
        isBulkEditTable={isBulkEditTable}
        setFullSelectedProducts={setFullSelectedProducts}
        setColumnFiltersParent={setColumnFilters}
        columnFiltersParent={columnFilters}
        bulkEditModalOpen={bulkEditModalOpen}
      />
    ) : null;

  const buttonText = buttonTextMapping[selected];

  const onHeaderButtonClick = (action?: string) => {
    const selections = Object.keys(selectedProducts);

    if (action === 'Revert') {
      setRevertModalOpen();
    } else if (selected === ProductTableType.published) {
      bulkUpdateProducts({
        data: { visible: false },
        deletePhotos: false,
        productIds: selections,
      });
      trackSubmit('hide');
      setBulkEditModalClose();
      setSelectedProducts({});
    } else if (selected === ProductTableType.hidden) {
      bulkUpdateProducts({
        data: { visible: true },
        deletePhotos: false,
        productIds: selections,
      });
      trackSubmit('make visible');
      setBulkEditModalClose();
      setSelectedProducts({});
    } else if (selected === ProductTableType.unpublished) {
      if (action === 'Archive') {
        bulkArchiveProducts(selections);
        trackSubmit('archive');
      } else {
        bulkPublishProducts(fullSelectedProducts);
        trackSubmit('publish');
      }
      setBulkEditModalClose();
      setSelectedProducts({});
    }
  };

  useEffect(() => {
    // hide & make visible
    if (bulkUpdateProductsSuccess) {
      toast.add({
        label: 'Bulk update succeeded!',
        variant: 'success',
      });
    }
    if (bulkUpdateProductsError) {
      toast.add({
        label: 'Bulk update failed!',
        variant: 'error',
      });
    }
    if (bulkUpdateProductsLoading) {
      toast.add({
        label: 'Updating products...',
        variant: 'success',
      });
    }

    // publish
    if (bulkPublishProductsSuccess) {
      toast.add({
        label: 'Bulk publish succeeded!',
        variant: 'success',
      });
    }
    if (bulkPublishProductsError) {
      toast.add({
        label: 'Bulk publish failed!',
        variant: 'error',
      });
    }
    if (bulkPublishProductsLoading) {
      toast.add({
        label: 'Publishing...',
        variant: 'success',
      });
    }

    // archive
    if (bulkArchiveProductsSuccess) {
      toast.add({
        label: 'Bulk archive succeeded!',
        variant: 'success',
      });
    }
    if (bulkArchiveProductsError) {
      toast.add({
        label: 'Bulk archive failed!',
        variant: 'error',
      });
    }
    if (bulkArchiveProductsLoading) {
      toast.add({
        label: 'Archiving...',
        variant: 'success',
      });
    }
  }, [
    bulkUpdateProductsSuccess,
    bulkUpdateProductsError,
    bulkUpdateProductsLoading,
    bulkPublishProductsLoading,
    bulkPublishProductsSuccess,
    bulkPublishProductsError,
    bulkArchiveProductsLoading,
    bulkArchiveProductsSuccess,
    bulkArchiveProductsError,
  ]);

  return (
    <>
      <Box width="100%" height="60%" ml={-64} position="absolute">
        <Flex justifyContent="space-between" mb={24} mx={64}>
          <Flex gap={16} alignItems="center">
            <ButtonToggleStylesOverrideWrapper>
              <ButtonToggle
                value={selected}
                onChange={(value) => {
                  if (isProductTableType(value)) {
                    setSelected(value);
                  }
                  setSelectedProducts({});
                }}
                full={false}
              >
                <ButtonToggle.Button
                  value={ProductTableType.published}
                  label="Published"
                />
                <ButtonToggle.Button
                  value={ProductTableType.hidden}
                  label="Hidden"
                />
                <ButtonToggle.Button
                  value={ProductTableType.unpublished}
                  label="Unpublished"
                />
              </ButtonToggle>
            </ButtonToggleStylesOverrideWrapper>
          </Flex>
          <Flex gap={16}>
            {showClearButton ? (
              <ClearPotenciesButtonWrapper>
                <Button
                  key="clear-potencies"
                  label="Clear potencies"
                  variant="secondary"
                  onClick={() => setClearModalOpen()}
                  disabled={!canEditProducts || hasArchivedFilter}
                />
              </ClearPotenciesButtonWrapper>
            ) : null}
            <BulkEditButtonWrapper>
              <Button
                key="bulk-edit"
                label="Bulk edit"
                variant="secondary"
                onClick={() => setBulkEditModalOpen()}
                disabled={!canEditProducts || hasArchivedFilter}
              />
            </BulkEditButtonWrapper>
            <AutoPublishButtonWrapper>
              <Button
                key="auto-publish"
                label="Manage auto-publish"
                variant="secondary"
                onClick={() => setAutoPublishModalOpen()}
              />
            </AutoPublishButtonWrapper>
          </Flex>
        </Flex>
        {table()}
      </Box>

      {bulkEditModalOpen ? (
        <BulkEditModal
          selectedProducts={selectedProducts}
          open={bulkEditModalOpen}
          headerButton={
            <HeaderButton
              text={buttonText}
              selectedProducts={selectedProducts}
              onClick={onHeaderButtonClick}
              productTableType={selected}
            />
          }
          productTable={table(true)}
          onClose={() => {
            setBulkEditModalClose();
            setSelectedProducts({});
          }}
        />
      ) : null}

      {autoPublishModalOpen ? (
        <AutoPublishModal
          open={autoPublishModalOpen}
          onClose={() => setAutoPublishModalClose()}
          productTypes={autoPublishSettings}
          storeName={storePayload?.store?.name}
        />
      ) : null}

      {revertModalOpen ? (
        <RevertModal
          open={revertModalOpen}
          onClose={setRevertModalClose}
          selectedProducts={selectedProducts}
          onBulkEditModalClose={setBulkEditModalClose}
          onSelectedProductsReset={setSelectedProducts}
        />
      ) : null}
      {clearModalOpen ? (
        <ClearModal
          open={clearModalOpen}
          onClose={setClearModalClose}
          onBulkEditModalClose={setBulkEditModalClose}
        />
      ) : null}
    </>
  );
};
